import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  useFieldArray,
  useForm,
  UseFormRegister,
  UseFormSetValue,
  UseFormClearErrors,
  UseFormTrigger,
  FieldErrors,
  FieldPath,
} from 'react-hook-form';
import { toast } from 'react-toastify';
import { firstErrorMessage } from '@/utils/functions/firstErrorMessage';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import {
  BUSINESS_CATEGORIES_BY_INDUSTRY,
  CompanyDataFormValues,
  INDUSTRY_OPTIONS,
  companyDataSchema,
  financialReportLabel,
  step1Schema,
  step2Schema,
  step1RulesSchema,
  step2RulesSchema,
  step3Schema,
} from './company-data.schema';
import { ICompanyData } from '@/types/company-data.types';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { useRegionCascade } from '@/hooks/useRegionCascade';
import { IProvince, IRegency } from '@/types/region.types';
import { useAutosave } from '@/hooks/useAutosave';
import DraftBanner from '@/components/form/DraftBanner';
import AutosaveIndicator from '@/components/form/AutosaveIndicator';
import { LivePreviewCard } from './LivePreviewCard';
import { RegionSelect } from './RegionSelect';
import { toTitleCase } from './region-options';

interface CompanyDataFormProps {
  defaultValues?: Partial<ICompanyData>;
  /**
   * May return a promise; when it does, the form awaits it so the local draft
   * is only cleared after the save actually succeeded.
   */
  onSubmit: (data: Record<string, unknown>) => void | Promise<unknown>;
  isSubmitting?: boolean;
  headerAction?: ReactNode;
}

type RegionCascade = {
  provinces: IProvince[];
  regencies: IRegency[];
  isLoadingProvinces: boolean;
  isLoadingRegencies: boolean;
  provincesError: boolean;
  regenciesError: boolean;
  retry: () => void;
  selectedProvince: string;
  selectedRegency: string;
  handleProvinceChange: (id: string) => void;
  handleRegencyChange: (id: string) => void;
  syncSelection: (provinceId?: string | null, regencyId?: string | null) => void;
  reset: () => void;
};

interface Step1Props {
  register: UseFormRegister<CompanyDataFormValues>;
  errors: FieldErrors<CompanyDataFormValues>;
  setValue: UseFormSetValue<CompanyDataFormValues>;
  watch: (name: string) => string;
  isReadOnly: boolean;
  regionCascade: RegionCascade;
  communityFields: FieldArrayWithId<CompanyDataFormValues, 'business_communities'>[];
  appendCommunity: UseFieldArrayAppend<CompanyDataFormValues, 'business_communities'>;
  removeCommunity: UseFieldArrayRemove;
}

interface Step2Props {
  register: UseFormRegister<CompanyDataFormValues>;
  errors: FieldErrors<CompanyDataFormValues>;
  setValue: UseFormSetValue<CompanyDataFormValues>;
  clearErrors: UseFormClearErrors<CompanyDataFormValues>;
  isReadOnly: boolean;
  businessRegionCascade: RegionCascade;
  ownershipStatus: string;
  businessCategoryOptions: readonly string[];
  watch: (name: string) => string;
}

interface Step3Props {
  register: UseFormRegister<CompanyDataFormValues>;
  errors: FieldErrors<CompanyDataFormValues>;
  watch: (name: string) => string;
  setValue: UseFormSetValue<CompanyDataFormValues>;
  trigger: UseFormTrigger<CompanyDataFormValues>;
  isReadOnly: boolean;
  socialMediaMarketplaceFields: FieldArrayWithId<CompanyDataFormValues, 'social_media_marketplaces'>[];
  appendSocialMediaMarketplace: UseFieldArrayAppend<CompanyDataFormValues, 'social_media_marketplaces'>;
  removeSocialMediaMarketplace: UseFieldArrayRemove;
}

const createBlankSocialMediaMarketplace = () => ({
  social_media_marketplace: '',
  account_name: '',
});

const OTHER_PRODUCT_SERVICE_ROW_COUNT = 5;
const OTHER_PRODUCT_SERVICE_ROWS = [
  { key: 'primary', required: true },
  { key: 'secondary-1', required: false },
  { key: 'secondary-2', required: false },
  { key: 'secondary-3', required: false },
  { key: 'secondary-4', required: false },
] as const;

const SOCIAL_MEDIA_OPTIONS = ['Instagram', 'TikTok', 'Facebook', 'Shopee', 'Tokopedia', 'Website Bisnis'] as const;

const STEPS = [
  { id: 1, label: 'Identitas Pemilik' },
  { id: 2, label: 'Identitas Bisnis' },
  { id: 3, label: 'Produk Usaha' },
] as const;

const STEP1_FIELDS: (keyof CompanyDataFormValues)[] = [
  'client_name',
  'client_nickname',
  'owner_phone',
  'owner_citizenship',
  'owner_foreign_domicile',
  'province_id',
  'province_name',
  'regency_id',
  'regency_name',
  'owner_gender',
];

const STEP2_FIELDS: (keyof CompanyDataFormValues)[] = [
  'business_name',
  'business_legal_name',
  'industry',
  'business_category',
  'business_category_other',
  'business_age_years',
  'branch_count',
  'ownership_status',
  'status_usaha',
  'partnership_count',
  'legal_entity',
  'total_employees',
  'business_scale',
  'business_in_indonesia',
  'business_foreign_location',
  'business_province_id',
  'business_province_name',
  'business_regency_id',
  'business_regency_name',
  'office_phone',
  'business_website',
  'head_office_address',
  'has_financial_report',
  'main_problem_1',
  'main_problem_2',
  'main_problem_3',
];

const STEP3_FIELDS: (keyof CompanyDataFormValues)[] = [
  'product_services_primary',
  'social_media_marketplaces',
  'other_product_services',
];

const STEP_FIELDS = [STEP1_FIELDS, STEP2_FIELDS, STEP3_FIELDS];

/** Kategori/industri yang mewajibkan keterangan tambahan (business_category_other). */
const needsCategoryOther = (industry?: string, category?: string) =>
  industry === 'Lainnya' || category === 'Lainnya' || category === 'Pendidikan Non-Formal Lainnya';

const normalizeOtherProductServices = (rows?: ICompanyData['other_product_services']) => {
  return Array.from({ length: OTHER_PRODUCT_SERVICE_ROW_COUNT }, (_, index) => {
    const estimatedPrice = rows?.[index]?.estimated_price || '';
    const currency: 'Rp' | '$' = estimatedPrice.trim().startsWith('$') ? '$' : 'Rp';
    const [estimated_price_min, estimated_price_max] = estimatedPrice.includes('-')
      ? estimatedPrice.split('-')
      : [estimatedPrice, ''];
    return {
      product_service_name: rows?.[index]?.product_service_name || '',
      currency,
      estimated_price_min: estimated_price_min ? formatCurrency(estimated_price_min, currency) : '',
      estimated_price_max: estimated_price_max ? formatCurrency(estimated_price_max, currency) : '',
    };
  });
};

const normalizeSocialMediaPlatform = (platform?: string) => {
  switch (platform?.toLowerCase()) {
    case 'ig':
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'facebook':
      return 'Facebook';
    case 'shopee':
      return 'Shopee';
    case 'tokopedia':
      return 'Tokopedia';
    case 'web':
    case 'website':
    case 'website bisnis':
      return 'Website Bisnis';
    default:
      return platform || '';
  }
};

const formatCurrency = (value: string, currency: string) => {
  const number = value.replace(/\D/g, '');
  if (!number) return '';
  const locale = currency === '$' ? 'en-US' : 'id-ID';
  return `${currency}${new Intl.NumberFormat(locale).format(Number(number))}`;
};

// Keeps a cleared number input as '' rather than the NaN `valueAsNumber` yields,
// so Zod reports the field's required message instead of a type error.
const numberFieldOptions = {
  setValueAs: (value: string) => (value === '' ? '' : Number(value)),
};

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed';

const selectClass =
  'w-full h-[38px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed';

const labelClass = 'block mb-1 text-sm font-medium dark:text-gray-200';

/** Label ringkas untuk kolom-kolom sempit pada baris Kategori Produk & harga. */
const priceLabelClass = 'block mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 truncate';

/**
 * Pemuatan daftar wilayah yang gagal sebelumnya hanya masuk console.error dan
 * menyisakan dropdown kosong — tidak terbedakan dari "provinsi ini memang tidak
 * punya kota". Tampilkan sebagai kegagalan, dan beri jalan keluar.
 */
const RegionLoadError = ({ show, label, onRetry }: { show: boolean; label: string; onRetry: () => void }) =>
  show ? (
    <p className="mt-1 text-sm text-red-600">
      Gagal memuat daftar {label}.{' '}
      <button type="button" onClick={onRetry} className="font-medium underline">
        Coba lagi
      </button>
    </p>
  ) : null;

export const CompanyDataForm = ({
  defaultValues,
  onSubmit,
  isSubmitting = false,
  headerAction,
}: CompanyDataFormProps) => {
  const isReadOnly = useIsReadOnly();
  const [currentStep, setCurrentStep] = useState(1);

  const educationBusinessCategories = BUSINESS_CATEGORIES_BY_INDUSTRY.Pendidikan;
  const legacyBusinessType = defaultValues?.business_type || '';
  const defaultBusinessCategory =
    defaultValues?.business_category ||
    (educationBusinessCategories.includes(legacyBusinessType as (typeof educationBusinessCategories)[number])
      ? legacyBusinessType
      : '');
  let defaultIndustry = '';
  if (defaultValues?.industry) {
    defaultIndustry = defaultValues.industry;
  } else if (defaultBusinessCategory) {
    defaultIndustry = 'Pendidikan';
  }

  const parseMainProblems = (mainProblems?: string): [string, string, string] => {
    if (!mainProblems) return ['', '', ''];
    const parts = mainProblems.split(/\n/).filter(p => p.trim().length > 0);
    return [parts[0] || '', parts[1] || '', parts[2] || ''];
  };
  const [defaultProblem1, defaultProblem2, defaultProblem3] = parseMainProblems(defaultValues?.main_problems);

  const defaultCommunities = (defaultValues?.business_condition_position || '')
    .split('\n')
    .filter(Boolean)
    .map(name => ({ name }));

  const regionCascade = useRegionCascade(defaultValues?.province_id, defaultValues?.regency_id);
  const businessRegionCascade = useRegionCascade(
    defaultValues?.business_province_id,
    defaultValues?.business_regency_id,
  );

  const formControl = useForm<CompanyDataFormValues>({
    resolver: zodResolver(companyDataSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      client_name: defaultValues?.client_name || '',
      client_nickname: defaultValues?.client_nickname || '',
      owner_phone: defaultValues?.owner_phone || '',
      // Records saved before this field existed carry a province, so they are WNI.
      owner_citizenship: defaultValues?.owner_citizenship || 'WNI',
      owner_foreign_domicile: defaultValues?.owner_foreign_domicile || '',
      province_id: defaultValues?.province_id || '',
      province_name: defaultValues?.province_name || '',
      regency_id: defaultValues?.regency_id || '',
      regency_name: defaultValues?.regency_name || '',
      owner_gender: defaultValues?.owner_gender || 'pria',
      owner_religion: defaultValues?.owner_religion || 'islam',
      marital_status: defaultValues?.marital_status || 'belum menikah',
      spouse_activity: defaultValues?.spouse_activity || '',
      self_description: defaultValues?.self_description || '',
      business_condition_position: defaultValues?.business_condition_position || '',
      business_communities: defaultCommunities.length > 0 ? defaultCommunities : [{ name: '' }],
      business_name: defaultValues?.business_name || '',
      business_legal_name: defaultValues?.business_legal_name || '',
      industry: defaultIndustry,
      business_category: defaultBusinessCategory,
      business_category_other: defaultValues?.business_category_other || '',
      // '' (not 0) so a fresh field is empty: typing into a prefilled 0 would
      // otherwise produce "05" / "090". ?? keeps a genuinely saved 0 visible.
      business_age_years: defaultValues?.business_age_years ?? '',
      branch_count: defaultValues?.branch_count ?? '',
      ownership_status: defaultValues?.ownership_status || 'Milik Pribadi',
      status_usaha: defaultValues?.status_usaha || 'Pusat',
      partnership_count: defaultValues?.partnership_count,
      legal_entity: defaultValues?.legal_entity || 'PT',
      total_employees: defaultValues?.total_employees ?? '',
      business_scale: defaultValues?.business_scale || '',
      business_in_indonesia: defaultValues?.business_in_indonesia || 'Ya',
      business_foreign_location: defaultValues?.business_foreign_location || '',
      business_province_id: defaultValues?.business_province_id || '',
      business_province_name: defaultValues?.business_province_name || '',
      business_regency_id: defaultValues?.business_regency_id || '',
      business_regency_name: defaultValues?.business_regency_name || '',
      office_phone: defaultValues?.office_phone || '',
      business_website: defaultValues?.business_website || '',
      head_office_address: defaultValues?.head_office_address || '',
      has_financial_report: defaultValues?.has_financial_report || '',
      product_services_primary: defaultValues?.product_services_primary || '',
      social_media_marketplaces: defaultValues?.social_media_marketplaces?.length
        ? defaultValues.social_media_marketplaces.map(item => ({
            ...item,
            social_media_marketplace: normalizeSocialMediaPlatform(item.social_media_marketplace),
          }))
        : [createBlankSocialMediaMarketplace()],
      other_product_services: normalizeOtherProductServices(defaultValues?.other_product_services),
      main_problem_1: defaultValues?.main_problem_1 || defaultProblem1,
      main_problem_2: defaultValues?.main_problem_2 || defaultProblem2,
      main_problem_3: defaultValues?.main_problem_3 || defaultProblem3,
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    setError,
    trigger,
    control,
    formState: { errors },
  } = formControl;

  // The parent remounts this component with a `key` once server data arrives,
  // so defaultValues are already populated on first render and autosave can
  // start immediately — there is no pre-load empty state to guard against.
  const autosave = useAutosave({
    form: formControl,
    formKey: 'company-data',
    enabled: !isReadOnly,
    entityId: defaultValues?.id,
  });

  // The Provinsi/Kota selects render from useRegionCascade's own state, which a
  // form.reset() cannot reach — the same split the WNA handler below works
  // around by calling reset() and setValue() together. Restoring a draft went
  // through form.reset() alone, so the two region selects came back blank and
  // the user read the whole restore as "datanya hilang".
  const handleRestoreDraft = useCallback(() => {
    autosave.restoreDraft();
    const restored = formControl.getValues();
    regionCascade.syncSelection(restored.province_id, restored.regency_id);
    businessRegionCascade.syncSelection(restored.business_province_id, restored.business_regency_id);
  }, [autosave, formControl, regionCascade, businessRegionCascade]);

  const {
    fields: socialMediaMarketplaceFields,
    append: appendSocialMediaMarketplace,
    remove: removeSocialMediaMarketplace,
  } = useFieldArray({ control, name: 'social_media_marketplaces' });

  const {
    fields: communityFields,
    append: appendCommunity,
    remove: removeCommunity,
  } = useFieldArray({ control, name: 'business_communities' });

  const ownershipStatus = watch('ownership_status');
  const selectedIndustry = watch('industry');
  const hasIndustryCategories = Boolean(selectedIndustry && selectedIndustry in BUSINESS_CATEGORIES_BY_INDUSTRY);
  const businessCategoryOptions = hasIndustryCategories
    ? BUSINESS_CATEGORIES_BY_INDUSTRY[selectedIndustry as keyof typeof BUSINESS_CATEGORIES_BY_INDUSTRY]
    : [];

  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToNextStep = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isTransitioning) return;
    setIsTransitioning(true);
    const fields = currentStep === 1 ? STEP1_FIELDS : STEP2_FIELDS;
    // Later steps can contain empty number fields. Parsing the whole form can
    // skip Zod refinements in that case, including this step's location rules.
    const schema = currentStep === 1 ? step1Schema : step2Schema;
    const rulesSchema = currentStep === 1 ? step1RulesSchema : step2RulesSchema;
    const values = formControl.getValues();
    const result = schema.safeParse(values);
    const rules = rulesSchema.safeParse(values);
    // Cross-field rules may already be in `result`; keep one issue per field.
    const issues = [...(result.error?.issues ?? []), ...(rules.error?.issues ?? [])].filter(
      (issue, index, all) => all.findIndex(other => other.path.join('.') === issue.path.join('.')) === index,
    );
    clearErrors(fields);
    if (issues.length === 0) {
      setCurrentStep(s => s + 1);
    } else {
      issues.forEach((issue, index) => {
        setError(
          issue.path.join('.') as FieldPath<CompanyDataFormValues>,
          {
            type: 'validation',
            message: issue.message,
          },
          { shouldFocus: index === 0 },
        );
      });
    }

    // Add a small delay before allowing the next click to prevent accidental double clicks hitting the next button
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const goToPrevStep = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentStep(s => s - 1);
  };

  const handleInvalidSubmit = (formErrors: FieldErrors<CompanyDataFormValues>) => {
    const stepIndex = STEP_FIELDS.findIndex(fields => fields.some(field => formErrors[field]));
    if (stepIndex < 0) {
      toast.error('Data belum lengkap, periksa kembali isian formulir.');
      return;
    }
    setCurrentStep(stepIndex + 1);
    const field = STEP_FIELDS[stepIndex].find(f => formErrors[f]);
    const message = (field && firstErrorMessage(formErrors[field])) || 'Periksa kembali isian pada langkah ini';
    toast.error(`Data belum lengkap di langkah "${STEPS[stepIndex].label}": ${message}`);
  };

  const handleFormSubmit = async (data: CompanyDataFormValues) => {
    const communities = (data.business_communities || []).map(c => c.name).filter(Boolean);
    const payload = {
      ...data,
      other_product_services: data.other_product_services.map(item => {
        // These carry Zod defaults, so z.input types them as optional here.
        const currency = item.currency || 'Rp';
        const priceMin = item.estimated_price_min || '';
        const priceMax = item.estimated_price_max || '';
        return {
          product_service_name: item.product_service_name,
          estimated_price: priceMax
            ? `${formatCurrency(priceMin, currency)} - ${formatCurrency(priceMax, currency)}`
            : formatCurrency(priceMin, currency),
        };
      }),
      owner_religion: data.owner_religion || 'islam',
      marital_status: data.marital_status || 'belum menikah',
      spouse_activity: data.spouse_activity || 'belum menikah',
      self_description: data.self_description || 'dummy data placeholder',
      // business_location is NOT NULL and min 3 chars server-side, so the
      // overseas free text has to flow into it rather than being left blank.
      business_location:
        data.business_in_indonesia === 'Tidak'
          ? data.business_foreign_location || ''
          : (data.business_regency_name && `${data.business_regency_name}, ${data.business_province_name}`) ||
            data.business_province_name,
      owner_address:
        data.owner_citizenship === 'WNA'
          ? data.owner_foreign_domicile || ''
          : `${data.regency_name || data.province_name}, Indonesia`,
      district_id: '',
      district_name: '',
      village_id: '',
      village_name: '',
      postal_code: '00000',
      ring1_team_count: 1,
      ring1_team_background: 'Tidak tersedia',
      has_erp_pos: 'Tidak',
      non_business_communication_position: '',
      status_usaha: data.status_usaha,
      has_financial_report: data.has_financial_report,
      has_debt_riba: 'Tidak',
      main_problems: [data.main_problem_1, data.main_problem_2, data.main_problem_3].join('\n'),
      business_condition_position: communities.join('\n'),
    };

    // Offline: the request would fail and lose the edits, so hold them locally
    // and let the user retry once the connection is back.
    if (!autosave.isOnline) {
      autosave.flush({ pendingSync: true });
      return;
    }

    // Await so a failed save leaves the draft in place — clearing it eagerly
    // would discard the user's work on the one path where they need it most.
    // The parent surfaces the error to the user; here we only decide the
    // draft's fate.
    try {
      await onSubmit(payload);
      autosave.clearDraft();
    } catch {
      autosave.flush({ pendingSync: true });
    }
  };

  const formValues = watch();

  const completedSteps: Record<number, boolean> = {
    1: step1Schema.safeParse(formValues).success,
    2: step2Schema.safeParse(formValues).success,
    3: step3Schema.safeParse(formValues).success,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-3 px-4 md:px-0">
        {!isReadOnly && <AutosaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt} />}
        {headerAction}
      </div>

      {autosave.pendingDraft && (
        <div className="px-4 md:px-0">
          <DraftBanner
            savedAt={autosave.pendingDraft.savedAt}
            pendingSync={autosave.pendingDraft.pendingSync}
            onRestore={handleRestoreDraft}
            onDiscard={autosave.discardDraft}
          />
        </div>
      )}

      <form noValidate onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)}>
        <div className="flex flex-col xl:flex-row gap-6 items-start">
          {/* Left: Wizard */}
          <div className="flex-1 min-w-0 w-full space-y-6">
            {/* Step content */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 space-y-6">
              {/* Section header (inside card per design) */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <h1 className="text-xl font-semibold">Konfigurasi Data Perusahaan</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Silakan lengkapi profil identitas pemilik bisnis dan badan usaha Anda sesuai data riil saat ini.
                </p>
              </div>

              {/* Step indicator */}
              <StepIndicator currentStep={currentStep} completedSteps={completedSteps} />

              {currentStep === 1 && (
                <Step1
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  watch={watch}
                  isReadOnly={isReadOnly}
                  regionCascade={regionCascade}
                  communityFields={communityFields}
                  appendCommunity={appendCommunity}
                  removeCommunity={removeCommunity}
                />
              )}
              {currentStep === 2 && (
                <Step2
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  isReadOnly={isReadOnly}
                  businessRegionCascade={businessRegionCascade}
                  ownershipStatus={ownershipStatus}
                  businessCategoryOptions={businessCategoryOptions}
                  watch={watch}
                />
              )}
              {currentStep === 3 && (
                <Step3
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  trigger={trigger}
                  isReadOnly={isReadOnly}
                  socialMediaMarketplaceFields={socialMediaMarketplaceFields}
                  appendSocialMediaMarketplace={appendSocialMediaMarketplace}
                  removeSocialMediaMarketplace={removeSocialMediaMarketplace}
                />
              )}
            </div>

            {/* Footer navigation */}
            <StepFooter
              currentStep={currentStep}
              isReadOnly={isReadOnly}
              isSubmitting={isSubmitting || isTransitioning}
              onNext={goToNextStep}
              onPrev={goToPrevStep}
            />
          </div>

          {/* Right: Live preview — sticky sidebar on desktop, stacked below on mobile */}
          <div className="w-full xl:w-[420px] shrink-0 xl:sticky xl:top-[110px]">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Live Preview
            </p>
            <LivePreviewCard data={formValues} />
          </div>
        </div>
      </form>
    </div>
  );
};

/* ─────────────────────── Step Indicator ─────────────────────── */
const StepIndicator = ({
  currentStep,
  completedSteps,
}: {
  currentStep: number;
  completedSteps: Record<number, boolean>;
}) => (
  <div className="flex items-center gap-0">
    {STEPS.map((step, idx) => {
      const isCompleted = completedSteps[step.id];
      const isActive = currentStep === step.id;
      return (
        <div key={step.id} className="flex items-center flex-1">
          <div className="flex flex-col items-center">
            <div
              translate="no"
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all shrink-0"
              style={{
                background: isCompleted || isActive ? '#1848FD' : '#E5E7EB',
                color: isCompleted || isActive ? '#fff' : '#6B7280',
              }}
            >
              {isCompleted ? <Check size={14} /> : step.id}
            </div>
            <span
              translate="no"
              className="text-[10px] sm:text-xs mt-1 font-medium text-center leading-tight"
              style={{ color: isActive || isCompleted ? '#1848FD' : '#9CA3AF' }}
            >
              {step.label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div
              className="flex-1 h-0.5 mx-2 mb-5 transition-all"
              style={{ background: isCompleted ? '#1848FD' : '#E5E7EB' }}
            />
          )}
        </div>
      );
    })}
  </div>
);

/* ─────────────────────── Step Footer ─────────────────────── */
const StepFooter = ({
  currentStep,
  isReadOnly,
  isSubmitting,
  onNext,
  onPrev,
}: {
  currentStep: number;
  isReadOnly: boolean;
  isSubmitting: boolean;
  onNext: (e?: React.MouseEvent) => void;
  onPrev: (e?: React.MouseEvent) => void;
}) => (
  <div className="flex items-center justify-between pt-2 pb-6">
    <div>
      {currentStep > 1 && (
        <button
          type="button"
          onClick={onPrev}
          disabled={isSubmitting}
          translate="no"
          className="icon-label-btn flex gap-2 px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <ArrowLeft size={16} />
          Kembali
        </button>
      )}
    </div>
    <div>
      {currentStep < 3 ? (
        <button
          type="button"
          onClick={onNext}
          disabled={isSubmitting}
          translate="no"
          className="icon-label-btn flex gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: '#FD6118' }}
        >
          Lanjut ke {STEPS[currentStep].label}
          <ArrowRight size={16} />
        </button>
      ) : (
        !isReadOnly && (
          <button
            type="submit"
            disabled={isSubmitting}
            translate="no"
            className="icon-label-btn flex gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: '#FD6118' }}
          >
            <Check size={16} />
            {isSubmitting ? 'Menyimpan...' : 'Simpan Data Perusahaan'}
          </button>
        )
      )}
    </div>
  </div>
);

const CustomCombobox = ({
  value,
  options,
  placeholder,
  manualInputPlaceholder,
  disabled,
  onChange,
  hasError,
}: {
  value: string;
  options: readonly string[];
  placeholder: string;
  manualInputPlaceholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  hasError?: boolean;
}) => {
  const normalizedValue = value ?? '';
  const [isOpen, setIsOpen] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) {
        setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const displayValue = normalizedValue.trim() ? normalizedValue : placeholder;

  return (
    <div ref={containerRef} className="!my-[5px]">
      <button
        ref={triggerRef}
        type="button"
        className={`text-left text-sm w-full h-[38px] px-3 border ${
          hasError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
        } rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed`}
        disabled={disabled}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span className={normalizedValue.trim() ? '' : 'text-gray-500'}>{displayValue}</span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPos?.top ?? 0,
              left: menuPos?.left ?? 0,
              width: menuPos?.width,
              maxWidth: 'calc(100vw - 2rem)',
              zIndex: 9999,
            }}
            className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg"
          >
            <div className="max-h-52 overflow-auto py-1">
              {options.map((option, index) => (
                <button
                  key={`${option}-${index}`}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>

            {manualInputPlaceholder && (
              <div className="border-t border-gray-200 dark:border-gray-600 p-2">
                <input
                  value={manualInput}
                  onChange={event => {
                    const val = event.target.value;
                    setManualInput(val);
                    onChange(val);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      setIsOpen(false);
                      event.currentTarget.blur();
                    }
                  }}
                  className="text-sm w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder={manualInputPlaceholder}
                />
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

/* ─────────────────────── Step 1: Identitas Pemilik ─────────────────────── */
const Step1 = ({
  register,
  errors,
  setValue,
  watch,
  isReadOnly,
  regionCascade,
  communityFields,
  appendCommunity,
  removeCommunity,
}: Step1Props) => {
  const citizenship = watch('owner_citizenship');
  const isForeignOwner = citizenship === 'WNA';

  // Switching to WNA clears the Indonesia region selection: reset() drops the
  // cascade's own state, while setValue clears what the form actually submits.
  const handleCitizenshipChange = (value: string) => {
    setValue('owner_citizenship', value as 'WNI' | 'WNA', { shouldValidate: true });

    if (value === 'WNA') {
      regionCascade.reset();
      setValue('province_id', '');
      setValue('province_name', '');
      setValue('regency_id', '');
      setValue('regency_name', '');
    } else {
      setValue('owner_foreign_domicile', '');
    }
  };

  return (
    <div className="space-y-5">
      <h2 className="font-semibold text-lg" style={{ color: '#1848FD' }}>
        Identitas Pemilik Bisnis (Pemilik)
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className={labelClass}>
            Nama Lengkap Pemilik <span className="text-red-500">*</span>
          </label>
          <input {...register('client_name')} disabled={isReadOnly} className={inputClass} />
          {errors.client_name && <p className="mt-1 text-sm text-red-600">{errors.client_name.message}</p>}
        </div>
        <div className="form-group">
          <label className={labelClass}>
            Nama Panggilan Pemilik <span className="text-red-500">*</span>
          </label>
          <input {...register('client_nickname')} disabled={isReadOnly} className={inputClass} />
          {errors.client_nickname && <p className="mt-1 text-sm text-red-600">{errors.client_nickname.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className={labelClass}>
            No WhatsApp <span className="text-red-500">*</span>
          </label>
          <input
            {...register('owner_phone')}
            placeholder="+62 atau 0812..."
            disabled={isReadOnly}
            className={inputClass}
          />
          {errors.owner_phone && <p className="mt-1 text-sm text-red-600">{errors.owner_phone.message}</p>}
        </div>

        <div className="form-group">
          <label className={labelClass}>
            Jenis Kelamin <span className="text-red-500">*</span>
          </label>
          <select {...register('owner_gender')} disabled={isReadOnly} className={selectClass}>
            <option value="pria">Pria</option>
            <option value="wanita">Wanita</option>
          </select>
          {errors.owner_gender && <p className="mt-1 text-sm text-red-600">{errors.owner_gender.message}</p>}
        </div>
      </div>

      <div className="form-group">
        <label className={labelClass}>
          Kewarganegaraan Pemilik <span className="text-red-500">*</span>
        </label>
        <select
          value={citizenship || 'WNI'}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleCitizenshipChange(e.target.value)}
          disabled={isReadOnly}
          className={selectClass}
        >
          <option value="WNI">WNI (Warga Negara Indonesia)</option>
          <option value="WNA">WNA (Warga Negara Asing)</option>
        </select>
        {errors.owner_citizenship && <p className="mt-1 text-sm text-red-600">{errors.owner_citizenship.message}</p>}
      </div>

      {isForeignOwner ? (
        <div className="form-group">
          <label className={labelClass}>
            Negara &amp; Kota Domisili Pemilik <span className="text-red-500">*</span>
          </label>
          <input
            {...register('owner_foreign_domicile')}
            placeholder="Misal: Kuala Lumpur, Malaysia"
            disabled={isReadOnly}
            className={inputClass}
          />
          {errors.owner_foreign_domicile && (
            <p className="mt-1 text-sm text-red-600">{errors.owner_foreign_domicile.message}</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-group">
            <label htmlFor="province_id" className={labelClass}>
              Provinsi <span className="text-red-500">*</span>
            </label>
            <RegionSelect
              value={regionCascade.selectedProvince}
              regions={regionCascade.provinces}
              notFoundMessage="Provinsi tidak ditemukan"
              placeholder={regionCascade.isLoadingProvinces ? 'Loading...' : 'Pilih Provinsi'}
              inputId="province_id"
              hasError={!!errors.province_id}
              onChange={provinceId => {
                regionCascade.handleProvinceChange(provinceId);
                setValue('province_id', provinceId, { shouldValidate: true });
                const selectedProvince = regionCascade.provinces.find(p => p.id === provinceId);
                setValue('province_name', selectedProvince?.name ? toTitleCase(selectedProvince.name) : '');
                setValue('regency_id', '');
                setValue('regency_name', '');
              }}
              disabled={isReadOnly || regionCascade.isLoadingProvinces}
            />
            {errors.province_id && <p className="mt-1 text-sm text-red-600">{errors.province_id.message}</p>}
            <RegionLoadError show={regionCascade.provincesError} label="Provinsi" onRetry={regionCascade.retry} />
          </div>

          <div className="form-group">
            <label htmlFor="regency_id" className={labelClass}>
              Kota Domisili Pemilik <span className="text-red-500">*</span>
            </label>
            <RegionSelect
              value={regionCascade.selectedRegency}
              regions={regionCascade.regencies}
              notFoundMessage="Kota/Kabupaten tidak ditemukan"
              placeholder={(() => {
                if (regionCascade.isLoadingRegencies) return 'Loading...';
                if (!regionCascade.selectedProvince) return 'Pilih Provinsi terlebih dahulu';
                if (regionCascade.regenciesError) return 'Gagal memuat Kota/Kabupaten';
                return 'Pilih Kota/Kabupaten';
              })()}
              inputId="regency_id"
              hasError={!!errors.regency_id}
              onChange={regencyId => {
                regionCascade.handleRegencyChange(regencyId);
                setValue('regency_id', regencyId, { shouldValidate: true });
                const selectedRegency = regionCascade.regencies.find(r => r.id === regencyId);
                setValue('regency_name', selectedRegency?.name ? toTitleCase(selectedRegency.name) : '');
              }}
              disabled={isReadOnly || !regionCascade.selectedProvince || regionCascade.isLoadingRegencies}
            />
            {errors.regency_id && <p className="mt-1 text-sm text-red-600">{errors.regency_id.message}</p>}
            <RegionLoadError show={regionCascade.regenciesError} label="Kota/Kabupaten" onRetry={regionCascade.retry} />
          </div>
        </div>
      )}

      {/* Komunitas Bisnis — dynamic list */}
      <div className="form-group space-y-2">
        <div className="flex items-center justify-between">
          <label className={labelClass}>
            Komunitas Bisnis yang Diikuti <span className="text-gray-400 font-normal">(Opsional)</span>
          </label>
          {!isReadOnly && (
            <button
              type="button"
              onClick={() => appendCommunity({ name: '' })}
              translate="no"
              className="icon-label-btn flex gap-1 text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Plus size={12} />
              Tambah Komunitas
            </button>
          )}
        </div>
        {communityFields.map((field, index) => (
          <div key={field.id} className="flex items-center gap-2">
            <input
              {...register(`business_communities.${index}.name`)}
              placeholder="Misal: HIPMI, TDA, Apindo"
              disabled={isReadOnly}
              className={`${inputClass} min-w-0`}
            />
            {!isReadOnly && communityFields.length > 1 && (
              <button
                type="button"
                onClick={() => removeCommunity(index)}
                className="p-2 rounded-lg border border-red-200 dark:border-red-900 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shrink-0"
              >
                <Trash2 size={14} />
              </button>
            )}
            {isReadOnly && communityFields.length > 1 && (
              <button
                type="button"
                disabled
                className="p-2 rounded-lg border border-red-200 text-red-300 cursor-not-allowed shrink-0"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────── Step 2: Identitas Bisnis ─────────────────────── */
const Step2 = ({
  register,
  errors,
  setValue,
  clearErrors,
  isReadOnly,
  businessRegionCascade,
  ownershipStatus,
  businessCategoryOptions,
  watch,
}: Step2Props) => {
  const selectedIndustry = watch('industry');
  const selectedBusinessCategory = watch('business_category');
  const businessInIndonesia = watch('business_in_indonesia');
  const isForeignBusiness = businessInIndonesia === 'Tidak';

  // Mirrors the owner handler: clear the region cascade when the business moves
  // abroad so a stale province cannot be submitted alongside the free text.
  const handleTerritoryChange = (value: string) => {
    setValue('business_in_indonesia', value as 'Ya' | 'Tidak', { shouldValidate: true });

    if (value === 'Tidak') {
      businessRegionCascade.reset();
      setValue('business_province_id', '');
      setValue('business_province_name', '');
      setValue('business_regency_id', '');
      setValue('business_regency_name', '');
    } else {
      setValue('business_foreign_location', '');
    }
  };

  const industryOptionsFiltered = INDUSTRY_OPTIONS.filter(o => o !== 'Lainnya');

  return (
    <div className="space-y-5">
      <h2 className="font-semibold text-lg" style={{ color: '#1848FD' }}>
        Identitas Bisnis
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className={labelClass}>
            Nama Bisnis (Brand) <span className="text-red-500">*</span>
          </label>
          <input {...register('business_name')} disabled={isReadOnly} className={inputClass} />
          {errors.business_name && <p className="mt-1 text-sm text-red-600">{errors.business_name.message}</p>}
        </div>

        <div className="form-group">
          <label className={labelClass}>
            Badan Hukum <span className="text-red-500">*</span>
          </label>
          <select {...register('legal_entity')} disabled={isReadOnly} className={selectClass}>
            <option value="PT">PT (Perseroan Terbatas)</option>
            <option value="CV">CV (Commanditaire Vennootschap)</option>
            <option value="UD">UD (Usaha Dagang)</option>
            <option value="Perorangan">Perorangan</option>
            <option value="Yayasan">Yayasan</option>
          </select>
          {errors.legal_entity && <p className="mt-1 text-sm text-red-600">{errors.legal_entity.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className={labelClass}>
            Nama Legal Bisnis <span className="text-red-500">*</span>
          </label>
          <input {...register('business_legal_name')} disabled={isReadOnly} className={inputClass} />
          {errors.business_legal_name && (
            <p className="mt-1 text-sm text-red-600">{errors.business_legal_name.message}</p>
          )}
        </div>

        <div className="form-group">
          <label className={labelClass}>
            Industri <span className="text-red-500">*</span>
          </label>
          <CustomCombobox
            value={selectedIndustry}
            options={industryOptionsFiltered}
            placeholder="Pilih Industri"
            manualInputPlaceholder="Jika tidak cocok, ketik industri manual"
            disabled={isReadOnly}
            hasError={!!errors.industry}
            onChange={val => {
              clearErrors('industry');
              clearErrors('business_category');
              setValue('industry', val, { shouldValidate: true });
              setValue('business_category', '', { shouldValidate: true });
            }}
          />
          {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry.message}</p>}
        </div>
      </div>

      <div className="form-group">
        <label className={labelClass}>
          Kategori Bisnis <span className="text-red-500">*</span>
        </label>
        {businessCategoryOptions.length > 0 ? (
          <CustomCombobox
            value={selectedBusinessCategory}
            options={businessCategoryOptions}
            placeholder={selectedIndustry ? 'Pilih Kategori Bisnis' : 'Pilih Industri Terlebih Dahulu'}
            disabled={isReadOnly || !selectedIndustry}
            hasError={!!errors.business_category}
            onChange={val => {
              clearErrors('business_category');
              setValue('business_category', val, { shouldValidate: true });
            }}
          />
        ) : (
          <input
            {...register('business_category')}
            placeholder="Ketik kategori manual"
            disabled={isReadOnly || !selectedIndustry}
            className={`${inputClass} ${errors.business_category ? 'border-red-500 ring-1 ring-red-500' : ''}`}
          />
        )}
        {errors.business_category && <p className="mt-1 text-sm text-red-600">{errors.business_category.message}</p>}
      </div>

      {needsCategoryOther(selectedIndustry, selectedBusinessCategory) && (
        <div className="form-group">
          <label className={labelClass} htmlFor="business_category_other">
            Keterangan Kategori Bisnis/Industri <span className="text-red-500">*</span>
          </label>
          <input
            id="business_category_other"
            {...register('business_category_other')}
            placeholder="Jelaskan kategori bisnis/industri Anda"
            disabled={isReadOnly}
            className={`${inputClass} ${errors.business_category_other ? 'border-red-500 ring-1 ring-red-500' : ''}`}
          />
          {errors.business_category_other && (
            <p className="mt-1 text-sm text-red-600">{errors.business_category_other.message}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className={labelClass}>
            Lama Usaha (Tahun) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            {...register('business_age_years', numberFieldOptions)}
            min="0"
            disabled={isReadOnly}
            className={inputClass}
          />
          {errors.business_age_years && (
            <p className="mt-1 text-sm text-red-600">{errors.business_age_years.message}</p>
          )}
        </div>
        <div className="form-group">
          <label className={labelClass}>
            Jumlah Cabang (Jika Ada) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            {...register('branch_count', numberFieldOptions)}
            min="0"
            disabled={isReadOnly}
            className={inputClass}
          />
          {errors.branch_count && <p className="mt-1 text-sm text-red-600">{errors.branch_count.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className={labelClass}>
            Status Kepemilikan Usaha <span className="text-red-500">*</span>
          </label>
          <select {...register('ownership_status')} disabled={isReadOnly} className={selectClass}>
            <option value="Milik Pribadi">Milik Pribadi</option>
            <option value="Syirkah">Syirkah (Kerja Sama)</option>
          </select>
          {errors.ownership_status && <p className="mt-1 text-sm text-red-600">{errors.ownership_status.message}</p>}
        </div>
        <div className="form-group">
          <label className={labelClass}>
            Status Usaha <span className="text-red-500">*</span>
          </label>
          <select {...register('status_usaha')} disabled={isReadOnly} className={selectClass}>
            <option value="Pusat">Pusat</option>
            <option value="Cabang">Cabang</option>
          </select>
          {errors.status_usaha && <p className="mt-1 text-sm text-red-600">{errors.status_usaha.message}</p>}
        </div>
      </div>

      {ownershipStatus === 'Syirkah' && (
        <div className="form-group">
          <label className={labelClass}>
            Jumlah Mitra (Berapa Orang) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            {...register('partnership_count', numberFieldOptions)}
            min="1"
            disabled={isReadOnly}
            className={inputClass}
          />
          {errors.partnership_count && <p className="mt-1 text-sm text-red-600">{errors.partnership_count.message}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className={labelClass}>
            Jumlah Karyawan Total <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            {...register('total_employees', numberFieldOptions)}
            min="1"
            disabled={isReadOnly}
            className={inputClass}
          />
          {errors.total_employees && <p className="mt-1 text-sm text-red-600">{errors.total_employees.message}</p>}
        </div>
        <div className="form-group">
          <label className={labelClass}>
            Skala Bisnis (Omset) Tahunan <span className="text-red-500">*</span>
          </label>
          <select {...register('business_scale')} disabled={isReadOnly} className={selectClass}>
            <option value="">Pilih Skala Omset Tahunan</option>
            <option value="< Rp 300 Juta">&lt; Rp 300 Juta</option>
            <option value="Rp 300 juta - Rp 2,5 miliar">Rp 300 juta - Rp 2,5 miliar</option>
            <option value="Rp 2,5 miliar - Rp 50 miliar">Rp 2,5 miliar - Rp 50 miliar</option>
            <option value="> Rp 50 miliar">&gt; Rp 50 miliar</option>
          </select>
          {errors.business_scale && <p className="mt-1 text-sm text-red-600">{errors.business_scale.message}</p>}
        </div>
      </div>

      <div className="form-group">
        <label className={labelClass}>
          Apakah usaha berada di wilayah Indonesia? <span className="text-red-500">*</span>
        </label>
        <select
          value={businessInIndonesia || 'Ya'}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleTerritoryChange(e.target.value)}
          disabled={isReadOnly}
          className={selectClass}
        >
          <option value="Ya">Ya, di wilayah Indonesia</option>
          <option value="Tidak">Tidak, di luar Indonesia</option>
        </select>
        {errors.business_in_indonesia && (
          <p className="mt-1 text-sm text-red-600">{errors.business_in_indonesia.message}</p>
        )}
      </div>

      {isForeignBusiness ? (
        <div className="form-group">
          <label className={labelClass}>
            Negara &amp; Kota Lokasi Usaha <span className="text-red-500">*</span>
          </label>
          <input
            {...register('business_foreign_location')}
            placeholder="Misal: Singapore, Singapura"
            disabled={isReadOnly}
            className={inputClass}
          />
          {errors.business_foreign_location && (
            <p className="mt-1 text-sm text-red-600">{errors.business_foreign_location.message}</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-group">
            <label htmlFor="business_province_id" className={labelClass}>
              Provinsi Lokasi Usaha <span className="text-red-500">*</span>
            </label>
            <RegionSelect
              value={businessRegionCascade.selectedProvince}
              regions={businessRegionCascade.provinces}
              notFoundMessage="Provinsi tidak ditemukan"
              placeholder={businessRegionCascade.isLoadingProvinces ? 'Loading...' : 'Pilih Provinsi'}
              inputId="business_province_id"
              hasError={!!errors.business_province_id}
              onChange={provinceId => {
                businessRegionCascade.handleProvinceChange(provinceId);
                setValue('business_province_id', provinceId, { shouldValidate: true });
                const selected = businessRegionCascade.provinces.find(p => p.id === provinceId);
                setValue('business_province_name', selected?.name ? toTitleCase(selected.name) : '');
                setValue('business_regency_id', '');
                setValue('business_regency_name', '');
              }}
              disabled={isReadOnly || businessRegionCascade.isLoadingProvinces}
            />
            {errors.business_province_id && (
              <p className="mt-1 text-sm text-red-600">{errors.business_province_id.message}</p>
            )}
            <RegionLoadError
              show={businessRegionCascade.provincesError}
              label="Provinsi"
              onRetry={businessRegionCascade.retry}
            />
          </div>

          <div className="form-group">
            <label className={labelClass}>
              Kabupaten/Kota Lokasi Usaha <span className="text-red-500">*</span>
            </label>
            <select
              value={businessRegionCascade.selectedRegency}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const regencyId = e.target.value;
                businessRegionCascade.handleRegencyChange(regencyId);
                setValue('business_regency_id', regencyId, { shouldValidate: true });
                const selected = businessRegionCascade.regencies.find(r => r.id === regencyId);
                setValue('business_regency_name', selected?.name ? toTitleCase(selected.name) : '');
              }}
              disabled={
                isReadOnly || !businessRegionCascade.selectedProvince || businessRegionCascade.isLoadingRegencies
              }
              className={selectClass}
            >
              <option value="">
                {(() => {
                  if (businessRegionCascade.isLoadingRegencies) return 'Loading...';
                  if (!businessRegionCascade.selectedProvince) return 'Pilih Provinsi terlebih dahulu';
                  if (businessRegionCascade.regenciesError) return 'Gagal memuat Kabupaten/Kota';
                  return 'Pilih Kabupaten/Kota';
                })()}
              </option>
              {businessRegionCascade.regencies.map(regency => (
                <option key={regency.id} value={regency.id}>
                  {toTitleCase(regency.name)}
                </option>
              ))}
            </select>
            {errors.business_regency_id && (
              <p className="mt-1 text-sm text-red-600">{errors.business_regency_id.message}</p>
            )}
            <RegionLoadError
              show={businessRegionCascade.regenciesError}
              label="Kabupaten/Kota"
              onRetry={businessRegionCascade.retry}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className={labelClass}>No Telepon Kantor</label>
          <input
            type="number"
            {...register('office_phone')}
            placeholder="Misal: 02188992211"
            disabled={isReadOnly}
            className={inputClass}
          />
          {errors.office_phone && <p className="mt-1 text-sm text-red-600">{errors.office_phone.message}</p>}
        </div>

        <div className="form-group">
          <label className={labelClass}>Website Bisnis</label>
          <input
            {...register('business_website')}
            placeholder="Misal: https://namabisnis.id"
            disabled={isReadOnly}
            className={inputClass}
          />
          {errors.business_website && <p className="mt-1 text-sm text-red-600">{errors.business_website.message}</p>}
        </div>
      </div>

      <div className="form-group">
        <label className={labelClass}>
          Alamat Kantor Pusat <span className="text-red-500">*</span>
        </label>
        <input
          {...register('head_office_address')}
          placeholder="Alamat lengkap kantor pusat"
          disabled={isReadOnly}
          className={inputClass}
        />
        {errors.head_office_address && (
          <p className="mt-1 text-sm text-red-600">{errors.head_office_address.message}</p>
        )}
      </div>

      <div className="form-group">
        <label className={labelClass}>
          Laporan Keuangan PSAK? <span className="text-red-500">*</span>
        </label>
        <select {...register('has_financial_report')} disabled={isReadOnly} className={selectClass}>
          <option value="">Pilih Status Laporan Keuangan</option>
          <option value="Sudah">{financialReportLabel('Sudah')}</option>
          <option value="Belum">{financialReportLabel('Belum')}</option>
          <option value="Sedang Proses">Sedang Proses</option>
        </select>
        {errors.has_financial_report && (
          <p className="mt-1 text-sm text-red-600">{errors.has_financial_report.message}</p>
        )}
      </div>

      {/* 3 Masalah Utama */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          3 Masalah Utama Bisnis yang Dihadapi Saat Ini <span className="text-red-500">*</span>
        </h3>
        {(['main_problem_1', 'main_problem_2', 'main_problem_3'] as const).map((field, i) => (
          <div key={field} className="form-group">
            <label htmlFor={field} className={labelClass}>
              Masalah Bisnis {i + 1} <span className="text-red-500">*</span>
            </label>
            <input
              id={field}
              {...register(field)}
              placeholder={`Masalah Bisnis ${i + 1}`}
              disabled={isReadOnly}
              className={inputClass}
            />
            {errors[field] && <p className="mt-1 text-sm text-red-600">{errors[field]?.message}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────── Step 3: Produk Usaha ─────────────────────── */
const Step3 = ({
  register,
  errors,
  watch,
  setValue,
  trigger,
  isReadOnly,
  socialMediaMarketplaceFields,
  appendSocialMediaMarketplace,
  removeSocialMediaMarketplace,
}: Step3Props) => (
  <div className="space-y-6">
    <h2 className="font-semibold text-lg" style={{ color: '#1848FD' }}>
      Produk &amp; Media Sosial
    </h2>

    <div className="form-group">
      <label className={labelClass}>
        Produk/Layanan Utama yang Dijual <span className="text-red-500">*</span>
      </label>
      <input
        {...register('product_services_primary')}
        placeholder="Jelaskan produk atau layanan utama yang dijual"
        disabled={isReadOnly}
        className={inputClass}
      />
      {errors.product_services_primary && (
        <p className="mt-1 text-sm text-red-600">{errors.product_services_primary.message}</p>
      )}
    </div>

    {/* Social Media / Marketplace */}
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            Akun Sosial Media / Marketplace <span className="text-red-500">*</span>
          </h3>
          <p className="text-xs text-gray-500">Minimal 1 akun, tambahkan jika bisnis aktif di lebih dari satu kanal.</p>
        </div>
        {!isReadOnly && (
          <button
            type="button"
            onClick={() => appendSocialMediaMarketplace(createBlankSocialMediaMarketplace())}
            aria-label="Tambah akun sosial media/marketplace"
            translate="no"
            className="icon-label-btn flex gap-1 text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <Plus size={12} />
            Tambah Akun
          </button>
        )}
      </div>

      {socialMediaMarketplaceFields.map((field, index) => {
        const isLastRequiredRow = socialMediaMarketplaceFields.length === 1;
        return (
          <div key={field.id} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label htmlFor={`social-platform-${index}`} className={labelClass}>
                    Platform Sosial Media / Marketplace <span className="text-red-500">*</span>
                  </label>
                  <select
                    id={`social-platform-${index}`}
                    {...register(`social_media_marketplaces.${index}.social_media_marketplace` as const)}
                    disabled={isReadOnly}
                    className={selectClass}
                  >
                    <option value="">Pilih platform</option>
                    {SOCIAL_MEDIA_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor={`social-account-${index}`} className={labelClass}>
                    Nama Akun <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={`social-account-${index}`}
                    {...register(`social_media_marketplaces.${index}.account_name` as const)}
                    disabled={isReadOnly}
                    placeholder="@namaakun / nama toko"
                    className={inputClass}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeSocialMediaMarketplace(index)}
                disabled={isReadOnly || isLastRequiredRow}
                className="p-2 rounded-lg border border-red-200 dark:border-red-900 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <Trash2 size={14} />
              </button>
            </div>
            {(errors.social_media_marketplaces?.[index]?.social_media_marketplace ||
              errors.social_media_marketplaces?.[index]?.account_name) && (
              <p className="mt-1 text-sm text-red-600">
                {errors.social_media_marketplaces[index]?.social_media_marketplace?.message ||
                  errors.social_media_marketplaces[index]?.account_name?.message}
              </p>
            )}
          </div>
        );
      })}
      {errors.social_media_marketplaces && !Array.isArray(errors.social_media_marketplaces) && (
        <p className="mt-1 text-sm text-red-600">{errors.social_media_marketplaces.message}</p>
      )}
    </div>

    {/* Other Product Services */}
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          Kategori Produk &amp; Estimasi Rentang Harga
        </h3>
        <p className="text-xs text-gray-500">Baris pertama wajib diisi, baris lain opsional.</p>
      </div>

      {OTHER_PRODUCT_SERVICE_ROWS.map((row, index) => (
        <div key={row.key} className="grid grid-cols-1 2xl:grid-cols-2 gap-3 items-start">
          <div className="form-group">
            <label className={priceLabelClass}>
              Kategori Produk {row.required && <span className="text-red-500">*</span>}
            </label>
            <input
              {...register(`other_product_services.${index}.product_service_name`)}
              value={watch(`other_product_services.${index}.product_service_name`) || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setValue(`other_product_services.${index}.product_service_name`, e.target.value, {
                  shouldValidate: true,
                })
              }
              placeholder={`Kategori Produk ${index + 1}`}
              className={inputClass}
            />
            {errors.other_product_services?.[index]?.product_service_name && (
              <p className="mt-1 text-sm text-red-600">
                {errors.other_product_services[index]?.product_service_name?.message}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="flex-1 !min-w-[120px] form-group">
              {/* Label tetap, bukan hanya placeholder: kolomnya sempit sehingga
                  "Harga Min" terpotong, dan placeholder hilang begitu diketik —
                  pengguna trial membacanya sebagai dua kolom "Harga" yang sama. */}
              <label className={priceLabelClass}>
                Harga Minimal{' '}
                {(row.required || !!watch(`other_product_services.${index}.product_service_name`)?.trim()) && (
                  <span className="text-red-500">*</span>
                )}
              </label>
              <input
                {...register(`other_product_services.${index}.estimated_price_min`)}
                value={watch(`other_product_services.${index}.estimated_price_min`) || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const currency = watch(`other_product_services.${index}.currency`) || 'Rp';
                  setValue(
                    `other_product_services.${index}.estimated_price_min`,
                    formatCurrency(e.target.value, currency),
                    {
                      shouldValidate: true,
                    },
                  );
                  if (watch(`other_product_services.${index}.estimated_price_max`)) {
                    trigger(`other_product_services.${index}.estimated_price_max`);
                  }
                }}
                disabled={isReadOnly}
                placeholder="Harga Min"
                className={inputClass}
              />
              {errors.other_product_services?.[index]?.estimated_price_min && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.other_product_services[index]?.estimated_price_min?.message}
                </p>
              )}
            </div>
            <div className="flex-1 !min-w-[120px] form-group">
              <label className={priceLabelClass}>
                Harga Maksimal{' '}
                {(row.required || !!watch(`other_product_services.${index}.product_service_name`)?.trim()) && (
                  <span className="text-red-500">*</span>
                )}
              </label>
              <input
                {...register(`other_product_services.${index}.estimated_price_max`)}
                value={watch(`other_product_services.${index}.estimated_price_max`) || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const currency = watch(`other_product_services.${index}.currency`) || 'Rp';
                  setValue(
                    `other_product_services.${index}.estimated_price_max`,
                    formatCurrency(e.target.value, currency),
                    {
                      shouldValidate: true,
                    },
                  );
                }}
                disabled={isReadOnly}
                placeholder="Harga Max"
                className={inputClass}
              />
              {errors.other_product_services?.[index]?.estimated_price_max && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.other_product_services[index]?.estimated_price_max?.message}
                </p>
              )}
            </div>
            <div className="w-20 shrink-0 form-group">
              <label className={priceLabelClass}>Mata Uang</label>
              <select
                {...register(`other_product_services.${index}.currency`)}
                value={watch(`other_product_services.${index}.currency`) || 'Rp'}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const newCurrency = e.target.value as 'Rp' | '$';
                  setValue(`other_product_services.${index}.currency`, newCurrency);

                  const min = watch(`other_product_services.${index}.estimated_price_min`);
                  const max = watch(`other_product_services.${index}.estimated_price_max`);
                  if (min) {
                    setValue(`other_product_services.${index}.estimated_price_min`, formatCurrency(min, newCurrency), {
                      shouldValidate: true,
                    });
                  }
                  if (max) {
                    setValue(`other_product_services.${index}.estimated_price_max`, formatCurrency(max, newCurrency), {
                      shouldValidate: true,
                    });
                  }
                }}
                disabled={isReadOnly}
                className={selectClass}
              >
                <option value="Rp">Rp</option>
                <option value="$">$</option>
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);
